"use client"

import { useEffect, useState } from 'react'
import Heading from '@/shared/components/ui/typography/heading'
import { DASHBOARD_ROUTE } from '@/shared/constants/pages-routes'
import ReusableSearchInput from '@/shared/components/ui/reusable-search-input'
import FormCard from '@/features/work-orders/ui/form-card'
import { ChevronLeft } from 'lucide-react'
import Link from 'next/link'
import DashboardContentWrapper from '@/shared/components/dashboard-content-wrapper'
import WorkOrderForm from '@/features/work-orders/components/forms/work-order-form'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { useRouter } from 'next/navigation'
import { useWorkScopes } from '@/features/work-orders/hooks/use-work-scopes'
import { useRegionAwareHref } from '@/shared/hooks/use-region-aware-href'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'
import PrintButton from '@/features/work-orders/components/print-button'

function WorkOrderCreatePage() {
  const { session, isLoading: sessionLoading } = useSession()
  const router = useRouter()
  const [selectedWorkScope, setSelectedWorkScope] = useState<string>('')
  const [workScopeError, setWorkScopeError] = useState<string>('')
  const { workScopeOptions, loading, error } = useWorkScopes()
  const workOrdersHref = useRegionAwareHref(DASHBOARD_ROUTE.workOrders)

  const validateWorkScope = () => {
    if (!selectedWorkScope) {
      setWorkScopeError('Work scope is required')
      return false
    }
    setWorkScopeError('')
    return true
  }

  const handleWorkScopeChange = (value: string) => {
    setSelectedWorkScope(value)
    if (value) {
      setWorkScopeError('')
    }
  }

  useEffect(() => {
    if (sessionLoading) return

    const canCreateWorkOrder =
      session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

    if (!canCreateWorkOrder) {
      router.push(workOrdersHref)
    }
  }, [session, sessionLoading, router, workOrdersHref])

  if (sessionLoading) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <div className="flex h-64 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </DashboardContentWrapper>
    )
  }

  if (error) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <div className="text-sm text-red-500">Error loading work scopes: {error}</div>
      </DashboardContentWrapper>
    )
  }

  return (
    <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-0.5">
          <Link href={workOrdersHref}>
            <ChevronLeft className="h-7 w-7" />
          </Link>
          <Heading>Create Work Order</Heading>
        </div>
        <PrintButton title="Print Work Order" />
      </div>
      <FormCard>
        <div>
          <ReusableSearchInput
            options={workScopeOptions}
            selectedValue={selectedWorkScope}
            onValueChange={handleWorkScopeChange}
            placeholder={loading ? 'Loading work scopes...' : 'Please select a work scope to continue *'}
            emptyText="No work scopes found."
            disabled={loading}
            loading={loading}
          />
          {workScopeError && (
            <p className="mt-1 text-[0.8rem] font-normal text-destructive">{workScopeError}</p>
          )}
        </div>
      </FormCard>

      <WorkOrderForm
        workOrderType={selectedWorkScope || undefined}
        selectedWorkScope={selectedWorkScope}
        onWorkScopeChange={setSelectedWorkScope}
        onValidateWorkScope={validateWorkScope}
      />
      <ScrollToTopFab />
    </DashboardContentWrapper>
  )
}

export default WorkOrderCreatePage
